import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useThemeSlice } from "../../../components/ui/StatusBadge.jsx";
import { ModalShellHeader } from "../../../components/modal/ModalShellHeader.jsx";
import { buildModalShellTokens } from "../../../components/modal/modalShellTheme.js";
import { ACCOUNT_USER_ROLES } from "../../../api/endpoints/accountPermissions.js";
import { listUsers } from "../../../api/endpoints/users.js";
import { Ico } from "../../../components/icons/icons.jsx";

const EMPTY_FORM = { userId: "", role: "" };

export function AddAccountUserModal({ open, onClose, onSave, saving = false }) {
  const { c, dark, accentHex: A } = useThemeSlice();
  const ap = useMemo(() => buildModalShellTokens(dark, c, A), [dark, c, A]);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  useLayoutEffect(() => {
    if (!open) return;
    setForm({ ...EMPTY_FORM });
    setErrors({});
    setSubmitError(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setUsersLoading(true);
    listUsers()
      .then((data) => {
        if (!cancelled) setUsers(data.filter((u) => u.status === "active"));
      })
      .finally(() => {
        if (!cancelled) setUsersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!open) return null;

  const selectedUser = users.find((u) => u.id === form.userId);

  const validate = () => {
    const next = {};
    if (!form.userId) next.userId = "Select a user";
    if (!form.role) next.role = "Select a role";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    if (!selectedUser) return;
    setSubmitError(null);
    try {
      await onSave({
        name: selectedUser.name,
        email: selectedUser.email,
        role: form.role,
      });
    } catch (err) {
      setSubmitError(err.message ?? "Failed to add user");
    }
  };

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 70, background: ap.overlay, backdropFilter: "blur(8px)" }} role="presentation" />
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-account-user-title"
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(440px, 96vw)",
          zIndex: 71,
          background: ap.cardGradient,
          border: `1px solid ${ap.cardBorder}`,
          borderRadius: 14,
          boxShadow: ap.cardShadowLg,
          overflow: "hidden",
        }}
      >
        <ModalShellHeader
          title="Add user"
          titleId="add-account-user-title"
          subtitle="Grant an existing user access to this account"
          icon={Ico.user}
          onClose={onClose}
          closeLabel="Close add user form"
          dark={dark}
          c={c}
          A={A}
          ap={ap}
        />

        <form onSubmit={handleSubmit} style={{ padding: "16px 20px 20px" }}>
          <SelectField
            label="User"
            value={form.userId}
            onChange={(userId) => setForm((prev) => ({ ...prev, userId }))}
            error={errors.userId}
            disabled={usersLoading || saving}
            placeholder={usersLoading ? "Loading users…" : "Select a user"}
            c={c}
          >
            {users.map((user) => (
              <option key={user.id} value={user.id}>{user.name}</option>
            ))}
          </SelectField>

          {selectedUser && (
            <div style={{ marginTop: -8, marginBottom: 14, padding: "10px 12px", borderRadius: 8, border: `1px solid ${c.cardBdr}`, background: c.hover }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: c.sub, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>Email</div>
              <div style={{ fontSize: 12.5, color: c.mid }}>{selectedUser.email}</div>
            </div>
          )}

          <SelectField
            label="Role"
            value={form.role}
            onChange={(role) => setForm((prev) => ({ ...prev, role }))}
            error={errors.role}
            disabled={saving}
            placeholder="Select a role"
            c={c}
          >
            {ACCOUNT_USER_ROLES.map((role) => (
              <option key={role} value={role}>{role}</option>
            ))}
          </SelectField>

          {submitError && (
            <p style={{ margin: "0 0 12px", fontSize: 12, color: "#EF4444", fontWeight: 600 }}>{submitError}</p>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              style={{ height: 36, padding: "0 14px", borderRadius: 8, border: `1px solid ${c.cardBdr}`, background: c.card, color: c.text, fontWeight: 600, cursor: saving ? "wait" : "pointer", opacity: saving ? 0.7 : 1 }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || usersLoading}
              style={{ height: 36, padding: "0 16px", borderRadius: 8, border: "none", background: A, color: "#fff", fontWeight: 700, cursor: saving || usersLoading ? "wait" : "pointer", opacity: saving || usersLoading ? 0.7 : 1 }}
            >
              {saving ? "Adding…" : "Add user"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

function SelectField({ label, value, onChange, error, disabled, placeholder, c, children }) {
  return (
    <label style={{ display: "block", marginBottom: 14 }}>
      <span style={{ display: "block", fontSize: 11, fontWeight: 700, color: c.sub, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.04em" }}>
        {label}
      </span>
      <div style={{ position: "relative" }}>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          style={{
            width: "100%",
            height: 38,
            padding: "0 34px 0 12px",
            borderRadius: 8,
            border: `1px solid ${error ? "#EF4444" : c.cardBdr}`,
            background: c.card,
            color: value ? c.text : c.sub,
            fontSize: 13,
            outline: "none",
            boxSizing: "border-box",
            fontFamily: "inherit",
            appearance: "none",
            WebkitAppearance: "none",
            cursor: disabled ? "wait" : "pointer",
          }}
        >
          <option value="">{placeholder}</option>
          {children}
        </select>
        <span style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", color: c.sub, pointerEvents: "none", display: "flex" }}>
          {Ico.chevDown}
        </span>
      </div>
      {error && <span style={{ display: "block", marginTop: 4, fontSize: 11, color: "#EF4444" }}>{error}</span>}
    </label>
  );
}
